import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { useDeleteQuizSet } from '@/api/gen/quiz/quiz.gen';
import { showShareModal } from '@/components';
import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import {
  buildFolderPlayHref,
  useShowSaveToFolderModal,
} from '@/shared/features/quiz-folder';
import { useCreateAssignmentDialog } from '@/shared/googleClassroom';
import { usePlayUrl } from '@/shared/hooks';
import { useLtiSelectAction } from '@/shared/lti/hooks/useLtiSelectAction';
import { useQuizListItemProvider } from '@/shared/quizList/hooks/QuizListItemContext';
import { type QuizCardMenuItem } from '@/shared/quizList/hooks/useGetQuizListItemButtons';
import { useQuizCardEventTrack } from '@/shared/quizList/hooks/useQuizCardEventTrack';
import { confirmDialog } from '@/ui_v3';

export const useQuizListItemAction = (quizSet: SearchQuizsetRes) => {
  const router = useRouter();
  const { user } = useUser();
  const { mutateAsync } = useDeleteQuizSet();
  const { t } = useTranslation(['common']);
  const { trackButtons } = useQuizCardEventTrack();
  /* 이 목록이 어느 폴더 안에서 그려지는지 — 재생 맥락. 폴더 밖 목록에서는 비어 있다. */
  const { folderPlayOrigin } = useQuizListItemProvider();
  const createAssignmentDialog = useCreateAssignmentDialog();
  const playUrl = usePlayUrl(quizSet.entryMapHashId);
  const { selectQuiz: selectLtiQuiz } = useLtiSelectAction();
  const showSaveToFolderModal = useShowSaveToFolderModal();

  const openPage = useOpenWindow();

  const handleShareModal = () => {
    return showShareModal({
      ...quizSet,
      shareUrl: playUrl,
      contentType: 'QUIZSET',
      contentId: quizSet.id,
      contentOwnerId: quizSet.authorId,
      spaceHashId: quizSet.spaceHashId,
      trackLocation: 'public',
    });
  };

  const openLmsPage = () => {
    openPage(`/lms/${quizSet.id}/learning-report`);
  };
  const deleteAction = () =>
    mutateAsync({
      data: {
        id: quizSet.id,
      },
    }).then(() => {
      router.reload();
    });

  const onClickEvent = (actionType: QuizCardMenuItem['onClickEventName']) => {
    trackButtons(actionType);
    switch (actionType) {
      case 'lti_select': {
        void selectLtiQuiz({
          quizSetId: quizSet.id,
          entryMapHashId: quizSet.entryMapHashId,
          quizTitle: quizSet.title,
        });
        break;
      }
      case 'google_assignment': {
        createAssignmentDialog.open({
          quizSet,
        });
        break;
      }
      case 'share':
        handleShareModal();
        break;
      case 'copy':
        void router.push(`/duplicate/${quizSet.id}`);
        break;
      case 'save_to_my_quiz': {
        /*
          비로그인이면 저장할 곳이 없다. 퀴즈 상세의 `내 퀴즈로 저장`(`useDuplicateHandler`)과
          같이 로그인으로 보내고 지금 화면으로 돌려보낸다.
        */
        if (!user) {
          void router.push({
            pathname: '/login',
            query: { callbackUrl: router.asPath },
          });
          break;
        }
        /*
          ⚠️ 여기가 **소유권으로 intent 를 정하는 유일한 자리**다. 다른 진입점은 버튼 자체가
          복사인지 이동인지를 알지만, 이 행은 남의 폴더에도 뜬다 — 그 폴더에 내 퀴즈가 담겨
          있으면 복사가 아니라 이동이어야 한다. 판정 기준은 폴더 주인이 아니라 **퀴즈의 작성자**다.
        */
        void showSaveToFolderModal({
          id: quizSet.id,
          intent: user.quizUserId === quizSet.authorId ? 'move' : 'copy',
          currentFolderId: quizSet.folderId,
        });
        break;
      }
      case 'move_to_folder':
        /*
          퀴즈는 최대 한 폴더에만 속한다. 목록 응답의 `folderId` 가 지금 소속이라,
          그 폴더는 대상에서 빼고(같은 폴더로 이동하면 서버가 INVALID_INPUT) `기본 내 퀴즈`
          선택지를 열어 둘 수 있다.
        */
        void showSaveToFolderModal({
          id: quizSet.id,
          intent: 'move',
          currentFolderId: quizSet.folderId,
        });
        break;
      case 'report':
        openLmsPage();
        break;
      case 'enter':
        /*
          폴더 안에서 시작한 재생이면 폴더를 이어 간다. 서버 `play/next` 는 (폴더, 지금 퀴즈)만
          받는 stateless 계약이라 **중간 퀴즈부터 시작해도** 그 지점부터 이어진다.

          `folderPlayOrigin` 이 없으면 폴더 밖에서 연 것이므로 단일 재생 — 그 퀴즈에 걸린
          개인 '다음 퀴즈' 설정이 그대로 적용된다.
        */
        void router.push(
          folderPlayOrigin
            ? buildFolderPlayHref({
                folderId: folderPlayOrigin,
                entryMapHashId: quizSet.entryMapHashId,
                /*
                  이 경로는 소유자 전용이다 — `enter` 항목은 `myQuiz` 메뉴에만 있고, 폴더
                  상세는 소유자에게만 그 메뉴를 준다. 남의 퀴즈를 맛보는 체험 모드가 아니다.
                */
                isExperience: false,
              })
            : `/play/${quizSet.entryMapHashId}`,
        );
        break;
      case 'update':
        void router.push(`/update/${quizSet.id}`);
        break;
      case 'delete':
        void confirmDialog
          .openAsync({
            header: {
              title: t('common:quizListItem.error.delete'),
            },
            confirmIntent: 'destructive',
            cancelIntent: 'neutral',
          })
          .then(result => {
            if (result) {
              void deleteAction();
            }
          });

        break;
    }
  };

  return {
    onClickEvent,
  };
};
